import React, {FunctionComponent, useCallback, useMemo, useState} from "react";
import {Box, DialogActions, DialogContent, IconButton, Typography} from "@mui/material";
import Button from "../../../../../../components/base/button/button";
import {ArrowBack} from "@mui/icons-material";
import ImportCsvFile, {
    ImportCsvFileRequiredField,
    ImportCsvFileSteps
} from "../../../../../../components/app-specific/import-csv-file";
import useIsMounted from "../../../../../../hooks/use-is-mounted";
import Utils from "../../../../../../../core/services/utils/utils";
import LoadingLottieAnimation from "../../../../../../components/app-specific/lottie-animations/loading";
import {PurchasePlanningInformationImportDialogDataEntry} from "./index";
import {useTranslation} from "react-i18next";
import {DISTRIBUTED_QTY_COLUMN_PREFIX, vendorCodeFromDistributedQtyColumn} from "../planning-draft";


const initialCsvFileState = {
    step: ImportCsvFileSteps.dropzone,
    parsedColumns: [] as string[],
    parsedRows: [] as any[],
}

export interface PurchasePlanningInformationImportDialogFileImportProps {
    onImport: (data: PurchasePlanningInformationImportDialogDataEntry[]) => void | Promise<void>;
    closeDialog: () => void;
}

const PurchasePlanningInformationImportDialogFileImport: FunctionComponent<PurchasePlanningInformationImportDialogFileImportProps> = (props) => {
    const {onImport, closeDialog} = props;
    const {t} = useTranslation('views', {keyPrefix: 'app-specific.purchase-planning-details'});
    const formId = useMemo(() => Utils.createUUID(), []);
    const [importCsvFileState, setImportCsvFileState] = useState(initialCsvFileState);
    const [importing] = useState(false);
    const isMounted = useIsMounted();

    const parseQuantity = useCallback((value: unknown) => {
        if (value == null || value === '')
            return undefined;
        const parsed = Number(value);
        return Number.isNaN(parsed) ? 0 : parsed;
    }, []);

    const fields = useMemo<ImportCsvFileRequiredField[]>(() => {
        const distributionFields: ImportCsvFileRequiredField[] = [];
        const seenVendors = new Set<string>();
        for (const column of importCsvFileState.parsedColumns ?? []) {
            const vendorCode = vendorCodeFromDistributedQtyColumn(column?.toString?.() ?? '');
            if (!vendorCode || seenVendors.has(vendorCode.toLowerCase()))
                continue;
            seenVendors.add(vendorCode.toLowerCase());
            distributionFields.push({
                name: `vendorDistribution:${vendorCode}`,
                title: `${DISTRIBUTED_QTY_COLUMN_PREFIX}${vendorCode}`,
                optional: true,
                generator: () => '',
                format: parseQuantity,
            });
        }
        return [
            {
                name: 'partNo',
                title: 'Part number',
                generator: () => '',
                format: (e) => e?.toString()?.toUpperCase()?.trim(),
            },
            {
                name: 'quantity',
                title: 'Quantity',
                generator: () => 0,
                format: (e) => {
                    const parsed = Number(e);
                    return Number.isNaN(parsed) ? 0 : parsed;
                },
            },
            {
                name: 'forecastedQty',
                title: t($ => $.forecastedQty),
                optional: true,
                generator: () => '',
                format: parseQuantity,
            },
            {
                name: 'note',
                title: 'Note',
                generator: () => "",
                format: (e) => e?.toString()?.trim(),
            },
            ...distributionFields,
        ];
    }, [importCsvFileState.parsedColumns, parseQuantity, t])

    /**
     * Callback function passed to the column mapper
     */
    const onDropzoneParsed = useCallback(({rows, columns}) => {
        setImportCsvFileState({
            step: ImportCsvFileSteps.columnMapper,
            parsedColumns: columns,
            parsedRows: rows,
        });
    }, [])

    /**
     * Validates the items in the file and returns the valid items.
     */
    const validateItems = useCallback(async (_parsedRows: any[]) => {
        const parsedRows = _parsedRows.filter(e => !!e.partNo).map((row): PurchasePlanningInformationImportDialogDataEntry => {
            const vendorDistributions = Object.entries(row)
                .filter(([key, value]) => key.startsWith('vendorDistribution:') && typeof value === 'number' && value > 0)
                .map(([key, value]) => ({
                    vendorCode: key.slice('vendorDistribution:'.length),
                    qty: value as number,
                }));
            return {
                partNo: row.partNo,
                quantity: row.quantity ?? 0,
                note: row.note,
                forecastedQty: typeof row.forecastedQty === 'number' ? row.forecastedQty : undefined,
                vendorDistributions: vendorDistributions.length ? vendorDistributions : undefined,
            };
        });
        return [parsedRows, []] as [PurchasePlanningInformationImportDialogDataEntry[], []];
    }, [])

    /**
     * Callback function passed to the column mapper. Validates the items and sets the review section state.
     */
    const onColumnMapperParsed = useCallback(async (parsedRows) => {
        const validationResult = await validateItems(parsedRows);
        if (!isMounted())
            return;
        if (!validationResult) {
            setImportCsvFileState(initialCsvFileState);
            return;
        }
        const [validEntries] = validationResult;
        if (validEntries.length) {
            closeDialog();
            onImport?.(validEntries);
        }
    }, [isMounted, onImport, validateItems, closeDialog])

    return (
        <>
            <DialogContent>
                {
                    importing &&
                    <LoadingLottieAnimation width={'80%'} className={''}/>
                }
                {
                    !importing &&
                    <>
                        <Box sx={{display: 'flex', alignItems: 'center', flexDirection: 'column', width: '100%'}}>
                            {
                                importCsvFileState.step === ImportCsvFileSteps.columnMapper &&
                                <>
                                    <Box sx={{display: 'flex', alignItems: 'center', gap: 1, width: '100%'}}>
                                        <IconButton
                                            onClick={() => setImportCsvFileState(initialCsvFileState)}
                                            color={'paper.secondary'}
                                        >
                                            <ArrowBack className={'icon normal'}/>
                                        </IconButton>
                                        <Typography variant={'body1'} fontWeight={600}>
                                            Column Mapping
                                        </Typography>
                                    </Box>
                                    <Typography variant={'body2'}
                                                sx={{marginBlockStart: 1, marginBlockEnd: 4, width: '100%'}}>
                                        Please map the columns to the fields so we can process the adjustments.
                                    </Typography>
                                </>
                            }
                            <Box sx={{width: '100%'}}>
                                <ImportCsvFile
                                    dropzoneNotes={undefined}
                                    sampleFileRowCount={undefined}
                                    columnMapperInitialValues={undefined}
                                    offsetStartingHeight={undefined}
                                    offsetEndingHeight={undefined}
                                    getScrollParent={undefined}
                                    step={importCsvFileState.step!}
                                    fields={fields}
                                    sampleFileTitle={"Purchase-planning-session-import-sample-file.csv"}
                                    parsedColumns={importCsvFileState.parsedColumns}
                                    parsedRows={importCsvFileState.parsedRows}
                                    onDropzoneParsed={onDropzoneParsed}
                                    onColumnMapperParsed={onColumnMapperParsed}
                                    formId={formId}
                                />
                            </Box>
                        </Box>
                    </>
                }
            </DialogContent>
            {
                importCsvFileState.step === ImportCsvFileSteps.columnMapper &&
                <DialogActions sx={{display: 'flex', justifyContent: 'space-between', p: 0, m: 0}}>
                    <Button
                        variant={'dialog'}
                        size={'medium'}
                        type={'button'}
                        color={'primary'}
                        borderRadius={'0 8px 0 8px'}
                        onClick={() => setImportCsvFileState(initialCsvFileState)}
                    >
                        Back
                    </Button>
                    <Button
                        autoFocus
                        variant={'dialog'}
                        size={'medium'}
                        color={'primary'}
                        type={'submit'}
                        borderRadius={'8px 0 8px 0'}
                        form={formId}
                    >
                        Proceed
                    </Button>
                </DialogActions>
            }
        </>
    );
}

export default PurchasePlanningInformationImportDialogFileImport;